import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const fmtCAD = (v: number) => v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
const n = (v: any) => Number(v) || 0;

/* ── Types ── */
interface BreakdownItem {
  label: string;
  type: "per_person" | "per_plate" | "per_hour" | "per_event" | "flat";
  rate?: number;
  qty?: number;
  total: number;
}

interface BreakdownSection {
  name: string;
  total: number;
  breakdown: BreakdownItem[];
}

/* ── Colors ── */
const C = {
  navy: "#0d1b2a", gold: "#c9a96e", slate: "#5c6b7e", slateLt: "#9ca3af",
  line: "#eeebe6", bg: "#faf8f4", cream: "#f7f5f1", white: "#fff",
  peach: "#ffebd3", redTx: "#9b2c2c", greenTx: "#2d6a35",
};

/* ── Format formula text ── */
const formatFormula = (item: BreakdownItem): string => {
  if (item.type === "flat" || item.type === "per_event") {
    return item.rate && item.qty ? `${fmtCAD(item.rate)} × ${item.qty}` : "Flat Fee";
  }
  if (!item.rate || !item.qty) return "";
  const unit = item.type === "per_person" ? "guests" : item.type === "per_plate" ? "plates" : item.type === "per_hour" ? "hrs" : "units";
  return `${fmtCAD(item.rate)} × ${item.qty} ${unit}`;
};

/* ── Short type label ── */
const typeTag = (t: string): string => {
  const map: Record<string, string> = { per_person: "pp", per_plate: "plate", per_hour: "hr", per_event: "event", flat: "flat" };
  return map[t] || t;
};

/* ── Build breakdown from booking data ── */
export function buildBreakdownSections(b: any): BreakdownSection[] {
  const guests = n(b.guestCount);
  const sections: BreakdownSection[] = [];

  // Hall
  if (n(b.hallSubtotal) > 0) {
    const halls = b.halls ?? [];
    const hallName = halls[0]?.hallLabel || halls[0]?.hallKey || "Hall";
    const items: BreakdownItem[] = [];
    if (n(b.hallFlatFee) > 0) {
      items.push({ label: `${hallName} — Base Fee`, type: "flat", total: n(b.hallFlatFee) });
    }
    if (n(b.hallPricePerPerson) > 0) {
      items.push({ label: `${hallName} — Per Person`, type: "per_person", rate: n(b.hallPricePerPerson), qty: guests, total: n(b.hallPricePerPerson) * guests });
    }
    if (items.length === 0) items.push({ label: hallName, type: "flat", total: n(b.hallSubtotal) });
    sections.push({ name: "Hall Rental", total: n(b.hallSubtotal), breakdown: items });
  }

  // Food & Menu
  if (n(b.foodSubtotal) > 0) {
    const items: BreakdownItem[] = [];
    if (b.foodServiceOption && n(b.foodServicePricePerPerson) > 0) {
      items.push({ label: b.foodServiceOption, type: "per_person", rate: n(b.foodServicePricePerPerson), qty: guests, total: n(b.foodServicePricePerPerson) * guests });
    }
    if (b.menuOption && n(b.menuPricePerPerson) > 0) {
      items.push({ label: b.menuOption, type: "per_person", rate: n(b.menuPricePerPerson), qty: guests, total: n(b.menuPricePerPerson) * guests });
    }
    if (items.length === 0) items.push({ label: "Food & Menu", type: "flat", total: n(b.foodSubtotal) });
    sections.push({ name: "Food & Menu", total: n(b.foodSubtotal), breakdown: items });
  }

  // Bar
  if (n(b.barSubtotal) > 0) {
    const items: BreakdownItem[] = [];
    if (b.barServiceOption && n(b.barServiceAmount) > 0) {
      items.push({ label: b.barServiceOption, type: "flat", total: n(b.barServiceAmount) });
    }
    if (b.barPackageOption && n(b.barPackagePricePerPerson) > 0) {
      items.push({ label: b.barPackageOption, type: "per_person", rate: n(b.barPackagePricePerPerson), qty: guests, total: n(b.barPackagePricePerPerson) * guests });
    }
    if (n(b.bartenderCount) > 0 && n(b.bartenderAmount) > 0) {
      const bCount = n(b.bartenderCount);
      const bRate = n(b.bartenderAmount);
      const bHrs = n(b.barHours);
      const bTotal = bCount * bRate;
      items.push({
        label: `${bCount} Bartender${bCount > 1 ? "s" : ""}${bHrs > 0 ? ` × ${bHrs} hrs` : ""}`,
        type: "per_hour", rate: bRate, qty: bCount, total: bTotal,
      });
    }
    if (items.length === 0) items.push({ label: "Bar Service", type: "flat", total: n(b.barSubtotal) });
    sections.push({ name: "Bar Service", total: n(b.barSubtotal), breakdown: items });
  }

  // LED
  const leds = b.selectedLEDs ?? [];
  if (leds.length > 0) {
    const LED_PRICES: Record<string, number> = { "LED 1": 3000, "LED 2": 3000, "LED 3": 1800, "LED 4": 1800 };
    const items = leds.map((led: string) => ({ label: led, type: "flat" as const, total: LED_PRICES[led] ?? 0 }));
    sections.push({ name: "LED & AV", total: n(b.ledSubtotal) || items.reduce((s: number, i: BreakdownItem) => s + i.total, 0), breakdown: items });
  }

  // Add-ons
  const addons = b.addons ?? [];
  if (addons.length > 0) {
    const items = addons.map((a: any) => ({
      label: `${a.itemName}${a.category ? ` (${a.category})` : ""}`,
      type: "flat" as const,
      rate: n(a.unitPrice),
      qty: n(a.quantity),
      total: n(a.lineTotal || a.quantity * a.unitPrice),
    }));
    sections.push({ name: "Add-ons", total: n(b.addonsSubtotal) || items.reduce((s: number, i: BreakdownItem) => s + i.total, 0), breakdown: items });
  }

  // Vendor Services — grouped by category
  const vendorItems = b.vendorItems ?? b.vendor_items ?? [];
  if (vendorItems.length > 0) {
    const catMap = new Map<string, any[]>();
    vendorItems.forEach((v: any) => {
      const cat = v.service_name_snapshot || v.serviceName || "Vendor Services";
      if (!catMap.has(cat)) catMap.set(cat, []);
      catMap.get(cat)!.push(v);
    });

    catMap.forEach((items, cat) => {
      const breakdownItems = items.map((v: any) => {
        const pt = v.pricing_type || v.pricingType || "per_event";
        return {
          label: `${v.item_name_snapshot || v.itemName || "Item"} — ${v.vendor_name_snapshot || v.vendorName || "Vendor"}`,
          type: pt as BreakdownItem["type"],
          rate: n(v.rate),
          qty: pt === "per_hour" ? n(v.hours) : n(v.quantity),
          total: n(v.final_price || v.finalPrice),
        };
      });
      const catTotal = breakdownItems.reduce((s: number, i: BreakdownItem) => s + i.total, 0);
      sections.push({ name: cat, total: catTotal, breakdown: breakdownItems });
    });
  }

  return sections;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ACCORDION SECTION
   ═══════════════════════════════════════════════════════════════════════════ */
const BreakdownAccordion: React.FC<{ section: BreakdownSection }> = ({ section }) => {
  const [open, setOpen] = useState(true); // expanded by default
  const hasBreakdown = section.breakdown.length > 1 || (section.breakdown.length === 1 && section.breakdown[0].label !== section.name);

  return (
    <Box sx={{ "&:not(:last-child)": { mb: 0.5 } }}>
      {/* Header row */}
      <Box onClick={() => hasBreakdown && setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 0.8, px: 0.5, cursor: hasBreakdown ? "pointer" : "default",
          borderRadius: "6px", transition: "background 0.12s",
          "&:hover": hasBreakdown ? { bgcolor: C.cream } : {},
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          {hasBreakdown && (
            <ExpandMoreIcon sx={{ fontSize: 16, color: C.slateLt, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
          )}
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.navy }}>{section.name}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy, fontVariantNumeric: "tabular-nums" }}>
          {fmtCAD(section.total)}
        </Typography>
      </Box>

      {/* Breakdown lines (expanded by default) */}
      {open && hasBreakdown && (
        <Box sx={{ pl: 3.5, pb: 0.8 }}>
          {section.breakdown.map((item, i) => {
            const formula = formatFormula(item);
            return (
              <Box key={i} sx={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline",
                py: "3px", "&:not(:last-child)": { borderBottom: `1px solid #f3f0ec` },
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11.5, color: "#3a3a3a" }}>
                    {item.label}
                    <Typography component="span" sx={{ fontSize: 10, color: C.slateLt, ml: 0.5 }}>({typeTag(item.type)})</Typography>
                  </Typography>
                  {formula && formula !== "Flat Fee" && (
                    <Typography sx={{ fontSize: 10, color: C.slateLt }}>{formula}</Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: "#3a3a3a", fontVariantNumeric: "tabular-nums", flexShrink: 0, ml: 2 }}>
                  {fmtCAD(item.total)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Thin separator */}
      <Box sx={{ height: 1, bgcolor: C.line, mx: 0.5 }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FINANCIAL BREAKDOWN (main export)
   ═══════════════════════════════════════════════════════════════════════════ */
const FinancialBreakdown: React.FC<{ booking: any }> = ({ booking }) => {
  const sections = useMemo(() => buildBreakdownSections(booking), [booking]);
  const totalDeposits = (booking.payments ?? []).reduce((s: number, p: any) => s + n(p.amount), 0);
  const balance = n(booking.grandTotal) - totalDeposits;

  return (
    <Box>
      {/* Service sections with accordion */}
      {sections.filter(s => s.total > 0).map(section => (
        <BreakdownAccordion key={section.name} section={section} />
      ))}

      {/* Adjustment */}
      {n(booking.adjustment) !== 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8, px: 0.5, borderBottom: `1px solid ${C.line}` }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: C.slate }}>Adjustment</Typography>
            {booking.adjustmentReason && <Typography sx={{ fontSize: 10, color: C.slateLt }}>{booking.adjustmentReason}</Typography>}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: n(booking.adjustment) < 0 ? C.redTx : C.navy, fontVariantNumeric: "tabular-nums" }}>
            {n(booking.adjustment) > 0 ? "+" : ""}{fmtCAD(n(booking.adjustment))}
          </Typography>
        </Box>
      )}

      {/* Service charge */}
      {n(booking.serviceChargePercent) > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8, px: 0.5, borderBottom: `1px solid ${C.line}` }}>
          <Typography sx={{ fontSize: 12, color: C.slate }}>Service Charge ({booking.serviceChargePercent}%)</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(booking.serviceChargeAmount))}</Typography>
        </Box>
      )}

      {/* Gratuity */}
      {n(booking.gratuityPercent) > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8, px: 0.5, borderBottom: `1px solid ${C.line}` }}>
          <Typography sx={{ fontSize: 12, color: C.slate }}>Gratuity ({booking.gratuityPercent}%)</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(booking.gratuityAmount))}</Typography>
        </Box>
      )}

      {/* Subtotal */}
      <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.8, px: 0.5, mt: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Subtotal</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(booking.subtotal))}</Typography>
      </Box>

      {/* HST */}
      <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 0.5 }}>
        <Typography sx={{ fontSize: 11.5, color: C.slateLt }}>HST (13%)</Typography>
        <Typography sx={{ fontSize: 11.5, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(booking.hstAmount))}</Typography>
      </Box>

      {/* Grand Total */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        mt: 1.5, py: 1.2, px: 2, borderRadius: "10px",
        background: `linear-gradient(135deg, ${C.peach} 0%, #fff0d9 100%)`,
        border: `1px solid ${C.gold}25`,
      }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.08em" }}>Grand Total</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(booking.grandTotal))}</Typography>
      </Box>

      {/* Payments summary */}
      {(totalDeposits > 0 || (booking.payments ?? []).length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: C.slate }}>Paid to date</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.greenTx, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(totalDeposits)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: balance > 0 ? C.redTx : balance < 0 ? "#1d4ed8" : C.greenTx }}>
              {balance > 0 ? "Balance owing" : balance < 0 ? "Credit / Overpaid" : "Paid in Full"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance > 0 ? C.redTx : balance < 0 ? "#1d4ed8" : C.greenTx, fontVariantNumeric: "tabular-nums" }}>
              {fmtCAD(Math.abs(balance))}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FinancialBreakdown;
